<template>
  <div>
    <es-loader
      id="pageLoader"
      v-if="loading"
      style="margin: 15rem"
    />
    <div v-else class="padding-top">
      <div class="is-flex justify-content-center is-fullHeight">
        <div>
          <div class="content justify-content-center">
            <h4>Log in to your account</h4>
          </div>
          <div class="login-container-p4-round-white">
            <div>
              <img src="@/assets/eScholar-logo-color.png" width="50" height="50" alt="eScholar Uniq-ID"/>
            </div>
            <form>
              <div class="field">
                <div class="field-body">
                  <div class="field has-text-left">
                    <div class="control">
                      <label for="formLoginEmail" class="label">Enter Email Address</label>
                      <input id="formLoginEmail"
                          class="input"
                          type="email"
                          v-model="email"
                          :class="{ 'is-danger': this.hasErrorEmail }"
                          required>
                    </div>
                    <span v-if="this.emailMsg" class="help is-danger">{{ emailMsg }}</span>
                  </div>
                </div>
              </div>
              <!-- <br/> -->
              <div class="field">
                <div class="field-body">
                  <div class="field has-text-left">
                    <div class="control">
                      <label for="formLoginPassword" class="label">Enter Password</label>
                      <input id="formLoginPassword"
                          class="input"
                          type="password" autocomplete="off"
                          v-model="password"
                          :class="{ 'is-danger': this.hasErrorPassword }"
                          required>
                    </div>
                    <p v-if="this.pwMsg" class="help is-danger">{{ pwMsg }}</p>
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="control">
                  <label class="checkbox">
                    <input type="checkbox" id="checkbox" v-model="rememberMeChkbox">
                      Remember Me
                </label>
              </div>
            </div>
              <div class="field">
                <button class="button is-primary is-fullwidth" :disabled="logging"
                  v-on:click.stop.prevent="formValidation">
                    <!-- <i class="fa fa-user"></i> -->
                      <span style="color:white;" v-if="logging">
                        <es-loader size="1x" :white="true"/>&nbsp;
                      </span>
                      Log In
                </button>
              </div>
              <div class="field has-text-left">
                <router-link to="/lostpassword">Lost your password?</router-link>
              </div>
            </form><!-- /form -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faBan,
  faTimesCircle,
} from '@fortawesome/pro-light-svg-icons';
import EmailUtils from '@/mixins/EmailUtils';
import NotificationManager from '@/mixins/NotificationManager';
import EsLoader from '@/components/EsLoader.vue';
import axios from 'axios';

library.add(
  faBan,
  faTimesCircle,
);

const LOGIN_STORAGE_KEY = 'rememberLoginInfo';

export default {
  components: {
    EsLoader,
  },
  mixins: [EmailUtils, NotificationManager],
  data() {
    return {
      loading: true,
      email: '',
      password: '',
      hasErrorEmail: false,
      hasErrorPassword: false,
      hasErrorValidation: false,
      submitted: false,
      messages: [],
      emailMsg: '',
      pwMsg: '',
      rememberMeChkbox: false,
      formErrorMsg: 'There were some errors. Please fix the errors highlighted in the fields below.',
      logging: false,
    };
  },
  watch: {
    email(value) {
      if (value) {
        this.hasErrorEmail = false;
      }
    },
    password(value) {
      if (value) {
        this.hasErrorPassword = false;
      }
    },
  },
  async created() {
    await this.onLoad();
  },
  mounted() {
    const storedInfo = JSON.parse(localStorage.getItem(LOGIN_STORAGE_KEY));
    if (storedInfo && storedInfo.email) {
      this.email = storedInfo.email;
      this.rememberMeChkbox = true;
    }
  },
  methods: {
    ...mapActions({
      validateLogin: 'validateLogin',
      initProperties: 'propertiesModule/init',
      initFields: 'fieldLabelsModule/init',
    }),
    async onLoad() {
      try {
        this.loading = true;
        // Check if LOCAL_AUTHENTICATION is enabled or not
        const url = '/login/sso/properties';
        const response = await axios.get(url);
        const ssoProperties = response.data;
        const isLocalAuthenticationEnabled = ssoProperties.LOCAL_AUTHENTICATION;
        if (isLocalAuthenticationEnabled !== 'true') {
          this.$router.push({ name: 'notAuthorized' });
        }
      } catch (error) {
        // ignore in case tomcat is not responding. If there is actually a problem, the user will
        // get an error when they try to login
      } finally {
        this.loading = false;
      }
    },
    async formValidation() {
      this.logging = true;
      let errors = 0;
      this.resetErrors();
      if (!this.email) {
        errors += 1;
        this.emailMsg = 'Email address is required.';
        this.hasErrorEmail = true;
      } else if (!this.isWelformed(this.email)) {
        errors += 1;
        this.emailMsg = 'Email address is invalid.';
        this.hasErrorEmail = true;
      } else {
        this.hasErrorEmail = false;
      }
      if (!this.password) {
        errors += 1;
        this.pwMsg = 'Password is required.';
        this.hasErrorPassword = true;
      }
      if (errors > 0) {
        this.messages.push(this.formErrorMsg);
        this.showNotification('danger', this.messages);
        this.logging = false;
      } else {
        const path = this.$route.query.r || '/';
        try {
          const response = await this.validateLogin({
            email: this.email,
            password: this.password,
          });

          if (response.forcePasswordChange) {
            this.$router.push(
              {
                name: 'resetpassword',
                params: { userEmail: response.email },
                query: { verificationKey: response.verificationKey },
              },
            );
          } else {
            await Promise.all([
              this.initProperties(),
              this.initFields(),
            ]);

            this.hideNotification();

            if (this.rememberMeChkbox) {
              const rememberLoginInfo = { email: this.email };
              localStorage.setItem(LOGIN_STORAGE_KEY, JSON.stringify(rememberLoginInfo));
            } else {
              localStorage.removeItem(LOGIN_STORAGE_KEY);
            }

            this.rememberMeChkbox = !this.rememberMeChkbox;

            this.$router.push(path);
          }
        } catch (error) {
          this.logging = false;
          this.message = error.response.data.message ? error.response.data.message : 'Error Logging In';
          this.messages.push(this.message);
          this.showNotification('danger', this.messages);
        }
      }
    },
    resetErrors() {
      this.messages = [];
      this.hasErrorPassword = false;
      this.hasErrorEmail = false;
      this.hasErrorValidation = false;
      this.pwMsg = null;
      this.emailMsg = null;
    },
  },
};
</script>

<style lang="sass" scoped>
  .login-container-p4-round-white
    border-radius: 4.8px
    padding: 1.5rem !important
    background-color: white
    width: 348px

  .justify-content-center
      -webkit-box-pack: center !important
      -ms-flex-pack: center !important
      justify-content: center !important

  .alert
    padding: .35rem 1rem
    //font-size: .75rem;
    &.alert-danger
      color: #dc3545
      background-color: #fdf4f5
      border-color: #b7313c
      border-radius: 0
      .close
        color: #b7313c

  .alert .close
    border: none
    background-color: transparent
    font-size: 1rem
    color: inherit
    font-weight: 700
    line-height: 1.2
    text-shadow: 0 1px 0 #fff
    opacity: 1
    &:hover
      color: #000

  .invalid-msg .invalid-feedback
    font-style: italic
    color: #dc3545
    font-size: .7rem
    padding: .25rem 0 .25rem .15rem

  .padding-top
    padding-top: 1rem
</style>
